import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';

const Icons = {
  Lock: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
  Book: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    </svg>
  ),
  Video: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="23 7 16 12 23 17 23 7"/>
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
    </svg>
  )
};

export default function GuestDashboard() {
  const navigate = useNavigate();
  const { resources } = useData();
  const previewResources = resources.slice(0, 2).map(resource => ({
    ...resource,
    summary: 'Preview chapter: stewardship foundations and key takeaways.',
    author: 'School of SHEM Library'
  }));
  const lockedResources = resources.slice(2, 6);
  const guestPages = [
    { label: 'Home', path: '/' },
    { label: 'About / Vision, Mission, Values', path: '/about' },
    { label: 'Why School of SHEM', path: '/why' },
    { label: 'Bible, Science, Transformation', path: '/bible-science' },
    { label: 'Competitive Advantage', path: '/advantage' },
    { label: 'Grading & Assessment', path: '/grading' },
    { label: 'Admissions', path: '/admissions' },
    { label: 'Student Code of Conduct', path: '/conduct' },
    { label: 'Curriculum', path: '/curriculum' },
    { label: 'Disclaimer', path: '/disclaimer' },
    { label: 'Enrollment & Fees', path: '/fees' },
    { label: 'Millionaires Connect', path: '/millionaires-connect' },
    { label: 'Our Team', path: '/team' },
    { label: 'Contact', path: '/contact' }
  ];

  return (
    <div className="page-content" style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div className="page-header">
        <h1 className="page-title">Guest Preview</h1>
        <p className="page-subtitle">Limited access view. Register to unlock the full portal.</p>
      </div>

      <div className="card card-gold" style={{ marginBottom: '24px' }}>
        <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-serif)', marginBottom: '8px' }}>Welcome, Guest</h2>
            <p style={{ opacity: 0.9 }}>
              You can preview selected lessons and resources. Full access unlocks live sessions, assignments, and LMS tools.
            </p>
          </div>
          <button className="btn" style={{ background: 'white', color: 'var(--primary)' }} onClick={() => navigate('/register')}>
            Register now for full access
          </button>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '24px' }}>
        {previewResources.length === 0 && (
          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <div className="card-body" style={{ textAlign: 'center', color: 'var(--gray-500)' }}>
              No preview resources yet. Check back soon.
            </div>
          </div>
        )}
        {previewResources.map(resource => (
          <div key={resource.id} className="card">
            <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--primary)' }}>
                {resource.type === 'video' ? <Icons.Video /> : <Icons.Book />}
                <div style={{ fontWeight: 600 }}>{resource.title}</div>
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>{resource.module}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>{resource.summary}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{resource.author}</div>
              <button className="btn btn-primary btn-sm">Preview</button>
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          marginBottom: '24px',
          padding: '24px',
          borderRadius: '20px',
          background: 'var(--surface-soft)',
          border: '1px solid rgba(212,175,55,0.3)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <div>
            <h3 style={{ fontSize: '1.25rem', fontFamily: 'var(--font-serif)', marginBottom: '6px' }}>Explore School of SHEM</h3>
            <p style={{ color: 'var(--gray-600)', maxWidth: '520px' }}>
              Browse the full prospectus experience. Each section is live and ready for review.
            </p>
          </div>
          <div className="badge badge-gold" style={{ alignSelf: 'flex-start' }}>Prospectus 5785</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
          {guestPages.map((page, index) => (
            <Link
              key={page.path}
              to={page.path}
              className="card"
              style={{
                padding: '14px 16px',
                borderRadius: '14px',
                border: '1px solid var(--gray-100)',
                boxShadow: 'var(--shadow-sm)',
                background: 'white',
                display: 'grid',
                gap: '6px',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                {String(index + 1).padStart(2, '0')}
              </span>
              <span style={{ fontWeight: 600 }}>{page.label}</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{page.path}</span>
              <span
                style={{
                  position: 'absolute',
                  right: '-18px',
                  top: '-18px',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(36,0,70,0.08)'
                }}
              />
            </Link>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 style={{ fontSize: '1.125rem' }}>Locked Content</h3>
        </div>
        <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
          {lockedResources.length === 0 && (
            <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
              No locked resources yet.
            </div>
          )}
          {lockedResources.map(resource => (
            <div key={resource.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderRadius: '10px', background: 'var(--gray-50)' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{resource.title}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{resource.module}</div>
              </div>
              <span className="badge badge-warning" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <Icons.Lock /> Locked
              </span>
            </div>
          ))}
          <button className="btn btn-primary" onClick={() => navigate('/register')}>
            Register now for full access
          </button>
        </div>
      </div>
    </div>
  );
}
